import { TimeZoneMismatchTooltip } from "@/components/partial/TimeZoneMismatchAlert";
import { DelegatedPermissionRequestContent } from "@/components/reserve/Reserve/DelegatedPermissionRequest";
import { Reserve } from "@/components/reserve/Reserve/Reserve";
import {
  useReserveForm
} from "@/components/reserve/Reserve/ReserveFormProvider";
import { ReserveOptions } from "@/components/reserve/Reserve/types";
import { IResource, ResourceType } from "@gettactic/api";
import { UseDisclosureReturn, cn } from "@gettactic/helpers/src";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { canScheduleOthers } from "@gettactic/helpers/src/permissions";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { isAllowedToReserve } from "@gettactic/helpers/src/resources/isAllowedToReserve";
import {
  useAuthenticated
} from "@gettactic/hooks";
import {
  useSaveResourceFavorite
} from "@gettactic/hooks";
import { MobileMapBottomMargin } from "@v2/components/display/Card/classes";
import { Image } from "@v2/components/display/Image/Image";
import { ResourceLabel } from "@v2/components/labels/ResourceLabel/ResourceLabel";
import { Switch } from "@v2/components/ui/Switch";
import { Button } from "@v2/components/ui/button";
import { ScrollArea } from "@v2/components/ui/scroll-area";
import { ArrowLeftIcon, FavoriteFilledIcon, FavoriteIcon } from "@v2/icons";
import React, { useCallback, useMemo } from "react";
import { isResourceAvailableByCapacity } from "./availabilityUtils";
export interface MapReserveCardProps
  extends Omit<ReserveOptions, "resource" | "resourceType"> {
  resourceType?: ResourceType;
  resource?: IResource | null;
  onCancel?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  dayViewDisclosure?: UseDisclosureReturn;
  requireDelegatedPermission?: boolean;
  msAuthUrl?: string;
  isCompact?: boolean;
  isModal?: boolean;
  onSuccess?: () => void;
  isOnReservationModal?: boolean;
}
const classes = {
  Card: `${MobileMapBottomMargin} absolute inset-0 z-sticky md:m-4 p-0 overflow-hidden data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out`,
  CardContent: "p-0 overflow-hidden h-full flex flex-col",
  CoverWrapper: "relative h-60 w-full overflow-hidden",
  CoverImage:
    "flex h-full w-full items-center justify-center text-center bg-neutral-200",
  CoverControlsWrapper: "absolute inset-0 top-5 md:pt-0 p-5",
  CoverControlsContainer: "flex justify-between",
  BackButton: "rounded-full bg-black/50",
  BackIcon: "size-4 text-white",
  FavoriteButton: "bg-white",
  FavoriteIcon: "size-4 text-neutral-600",
  ScrollArea: "flex flex-1",
  ScrollAreaContent: "flex flex-col gap-6 p-3 pt-6 pr-1",

  // card should fade gradient to top
  ActionButtonsWrapper:
    "p-3 flex flex-col gap-2.5 from-white/100 via-white/80 bg-gradient-to-t from-white to-transparent"
};

export const MapReserveCardInner: React.FC<
  MapReserveCardProps &
    Required<Pick<MapReserveCardProps, "resource">> & {
      haveAvailableSlots: boolean;
      resourceSchedules: any;
    }
> = ({
  resource,
  onCancel,
  dayViewDisclosure,
  msAuthUrl,
  requireDelegatedPermission,
  haveAvailableSlots,
  resourceSchedules
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint("md");
  const { form, submitReservation, isOnReservationModal } = useReserveForm();
  const onSubmit = form.handleSubmit(submitReservation);
  const coverImageUrl = resource
    ? getResourceCoverUrl(resource, 300)
    : undefined;
  const { toggleResourceFavorite, isLoading: isFavoriteLoading } =
    useSaveResourceFavorite();

  // const isDemo = React.useMemo(() => isDemoDomain(), []);

  const { startTime, durationMins, currentOffice } = useReserveForm();

  const officeTimeZone = currentOffice?.time_zone ?? "";
  const office = currentOffice;
  const entity = resource?.type;
  const startDate = startTime;
  const resourceType = resource?.type;

  const allowReserveByMaxDate = useMemo(() => {
    return isAllowedToReserve(
      startDate,
      officeTimeZone,
      office,
      resourceType ?? ("workspace" as ResourceType),
      authenticatedUser.user?.organization_user?.roles
    );
  }, [startDate, officeTimeZone, office, resource?.id, durationMins]);
  const allowReserveOnBehalf = canScheduleOthers(authenticatedUser);

  const meetingsDisabled =
    !authenticatedUser?.organization?.meeting_room_bookings_enabled;

  // Recalculate availability using client-side check when in reservation modal
  const actuallyHaveAvailableSlots = React.useMemo(() => {
    if (isOnReservationModal && resource && startTime && durationMins) {
      const schedules = resourceSchedules || [];
      return isResourceAvailableByCapacity(
        resource,
        schedules.flat(),
        startTime,
        durationMins
      );
    }
    return haveAvailableSlots;
  }, [
    isOnReservationModal,
    resource,
    startTime,
    durationMins,
    resourceSchedules,
    haveAvailableSlots
  ]);
  const allowReserve = React.useMemo(() => {
    if (!resource || !resource.is_available) {
      return false;
    }
    const result =
      allowReserveByMaxDate &&
      actuallyHaveAvailableSlots &&
      (resource.type !== "meeting_room" || !meetingsDisabled);
    return result;
  }, [
    allowReserveByMaxDate,
    meetingsDisabled,
    resource,
    actuallyHaveAvailableSlots
  ]);

  const handleConnectOutlook = useCallback(() => {
    window.open(msAuthUrl);
  }, [msAuthUrl]);

  return (
    <>
      <div className={classes.CoverWrapper}>
        {coverImageUrl && (
          <Image
            src={coverImageUrl}
            wrapperClassName={classes.CoverImage}
            className={"h-full object-contain"}
          />
        )}
        <div className={classes.CoverControlsWrapper}>
          <div className={classes.CoverControlsContainer}>
            <Button
              variant={"circleIcon"}
              className={classes.BackButton}
              onClick={onCancel}
              size={"icon"}
            >
              <ArrowLeftIcon className={classes.BackIcon} />
            </Button>
            {!requireDelegatedPermission && (
              <Button
                disabled={isFavoriteLoading}
                variant={"circleIcon"}
                className={classes.FavoriteButton}
                size={"icon"}
                onClick={async (event) => {
                  event.stopPropagation();
                  event.preventDefault();

                  if (resource) await toggleResourceFavorite(resource);
                }}
              >
                {resource?.favorite_id ? (
                  <FavoriteFilledIcon className={classes.FavoriteIcon} />
                ) : (
                  <FavoriteIcon className={classes.FavoriteIcon} />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      {requireDelegatedPermission ? (
        <div className="p-3">
          <DelegatedPermissionRequestContent
            msAuthUrl={msAuthUrl ?? ""}
            handleConnectOutlook={handleConnectOutlook}
          />
        </div>
      ) : (
        <>
          <ScrollArea className={classes.ScrollArea}>
            <div className={classes.ScrollAreaContent}>
              <div className={"flex w-full items-center gap-0"}>
                {resource && (
                  <ResourceLabel
                    className={"flex-1"}
                    infoWrapperClassName={"gap-[8px]"}
                    resource={resource}
                    showPicture={false}
                    allowFavorite={false}
                    headingClassName={"text-[18px]"}
                    hierarchyRight={
                      <TimeZoneMismatchTooltip
                        resourceOfficeId={resource.office_id}
                      />
                    }
                    headerRight={
                      dayViewDisclosure ? (
                        <Switch
                          onCheckedChange={dayViewDisclosure.onOpenChange}
                          checked={dayViewDisclosure.isOpen}
                          variant={"squared"}
                          size={"xs"}
                        />
                      ) : undefined
                    }
                  />
                )}
                <div />
              </div>
              <div className="v2-reservation">
                <Reserve showActions={false} isCompact={true} />
              </div>
            </div>
            {!isMd && <div className={"h-10"} />}
          </ScrollArea>
          <div
            className={cn(
              classes.ActionButtonsWrapper,
              !isMd && "fixed bottom-0 left-2"
            )}
          >
            <Button
              type={"submit"}
              onClick={onSubmit}
              disabled={isOnReservationModal ? !allowReserve : false}
            >
              {"Reserve"}
            </Button>
          </div>
        </>
      )}
    </>
  );
};